import { useState, useEffect, useCallback } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Eye, Users, MousePointerClick, Globe, TrendingUp, Monitor, Smartphone, Tablet, Bot, Download, Calendar, Filter } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { authFetch } from '../../lib/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============ SMALL COMPONENTS ============

const StatCard = ({ label, value, sub, icon: Icon, color }) => (
  <div className="p-5 rounded-xl border border-white/5 bg-[#0f0f17]" data-testid={`stat-${label}`}>
    <div className="flex items-center justify-between mb-3">
      <p className="text-[#71717a] text-sm">{label}</p>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
    <p className="text-2xl font-bold text-[#f5f5f5]">{value}</p>
    {sub && <p className="text-[#52525b] text-xs mt-1">{sub}</p>}
  </div>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a1a2e] border border-white/10 rounded-lg p-3 text-xs shadow-xl">
      <p className="text-[#a1a1aa] mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-medium">{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

const SectionTitle = ({ children }) => (
  <h2 className="text-[#f5f5f5] font-semibold text-base">{children}</h2>
);

// ============ HEATMAP ============

const Heatmap = ({ data }) => {
  if (!data || !data.grid) return null;
  const max = Math.max(...data.grid.flat(), 1);

  const getColor = (val) => {
    if (val === 0) return 'bg-[#1a1a2e]';
    const intensity = val / max;
    if (intensity < 0.25) return 'bg-[#3b82f6]/20';
    if (intensity < 0.5) return 'bg-[#3b82f6]/40';
    if (intensity < 0.75) return 'bg-[#3b82f6]/60';
    return 'bg-[#3b82f6]';
  };

  return (
    <div className="overflow-x-auto" data-testid="heatmap">
      <div className="min-w-[600px]">
        {/* Hours header */}
        <div className="flex items-center mb-1">
          <div className="w-20 shrink-0" />
          {data.hours.filter((_, i) => i % 2 === 0).map(h => (
            <div key={h} className="flex-1 text-center text-[#52525b] text-[10px]" style={{ minWidth: '24px' }}>
              {h}h
            </div>
          ))}
        </div>
        {/* Grid rows */}
        {data.days.map((day, di) => (
          <div key={day} className="flex items-center mb-0.5">
            <div className="w-20 shrink-0 text-[#71717a] text-xs pr-2 text-right">{day}</div>
            <div className="flex flex-1 gap-0.5">
              {data.grid[di].map((val, hi) => (
                <div
                  key={hi}
                  className={`flex-1 h-5 rounded-sm ${getColor(val)} transition-colors cursor-default`}
                  title={`${day} ${hi}h : ${val} visite${val > 1 ? 's' : ''}`}
                  style={{ minWidth: '12px' }}
                />
              ))}
            </div>
          </div>
        ))}
        {/* Legend */}
        <div className="flex items-center justify-end gap-1 mt-2">
          <span className="text-[#52525b] text-[10px] mr-1">Moins</span>
          <div className="w-3 h-3 rounded-sm bg-[#1a1a2e]" />
          <div className="w-3 h-3 rounded-sm bg-[#3b82f6]/20" />
          <div className="w-3 h-3 rounded-sm bg-[#3b82f6]/40" />
          <div className="w-3 h-3 rounded-sm bg-[#3b82f6]/60" />
          <div className="w-3 h-3 rounded-sm bg-[#3b82f6]" />
          <span className="text-[#52525b] text-[10px] ml-1">Plus</span>
        </div>
      </div>
    </div>
  );
};

// ============ MAIN ============

const AdminAnalytics = () => {
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [geo, setGeo] = useState(null);
  const [visitors, setVisitors] = useState([]);
  const [heatmap, setHeatmap] = useState(null);
  const [period, setPeriod] = useState(30);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [pageFilter, setPageFilter] = useState('');
  const [customRange, setCustomRange] = useState(false);

  const fetchAll = useCallback(() => {
    authFetch(`${API_URL}/api/analytics/summary`).then(r => r.json()).then(setSummary).catch(() => {});
    authFetch(`${API_URL}/api/analytics/funnel`).then(r => r.json()).then(setFunnel).catch(() => {});
    authFetch(`${API_URL}/api/analytics/geo`).then(r => r.json()).then(setGeo).catch(() => {});
    authFetch(`${API_URL}/api/analytics/visitors`).then(r => r.json()).then(setVisitors).catch(() => {});
    authFetch(`${API_URL}/api/analytics/heatmap`).then(r => r.json()).then(setHeatmap).catch(() => {});
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    let url = `${API_URL}/api/analytics/trend`;
    const params = new URLSearchParams();
    if (customRange && startDate && endDate) {
      params.set('start_date', startDate);
      params.set('end_date', endDate);
    } else {
      params.set('days', period);
    }
    if (pageFilter) params.set('page_filter', pageFilter);
    authFetch(`${url}?${params}`).then(r => r.json()).then(setTrend).catch(() => {});
  }, [period, startDate, endDate, pageFilter, customRange]);

  const exportCSV = async () => {
    const res = await authFetch(`${API_URL}/api/analytics/export`);
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'cloneia_analytics.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (!summary) return <div className="p-8 text-[#71717a]">Chargement...</div>;

  const funnelData = funnel ? [
    { name: 'Visiteurs', value: funnel.visitors, pct: 100 },
    { name: 'Scroll 50%+', value: funnel.scrolled, pct: funnel.visitors ? Math.round(funnel.scrolled / funnel.visitors * 100) : 0 },
    { name: 'Clic CTA', value: funnel.cta_clicks, pct: funnel.visitors ? Math.round(funnel.cta_clicks / funnel.visitors * 100) : 0 },
    { name: 'Soumission', value: funnel.form_submissions, pct: funnel.visitors ? Math.round(funnel.form_submissions / funnel.visitors * 100) : 0 },
  ] : [];

  const trendFormatted = trend.map(d => ({ ...d, label: d.date?.slice(5) }));
  const pageOptions = summary.pages?.map(p => p.page) || [];

  return (
    <div className="p-6 md:p-8 space-y-8" data-testid="admin-analytics">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-[#f5f5f5]">Analytics</h1>
        <Button
          onClick={exportCSV}
          className="inline-flex items-center gap-2 bg-[#0f0f17] border border-white/10 hover:border-[#3b82f6]/50 text-[#f5f5f5] px-4 py-2 rounded-lg text-sm"
          data-testid="export-csv"
        >
          <Download className="w-4 h-4" /> Exporter CSV
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard label="Vues (30j)" value={summary.month_views ?? 0} icon={Eye} color="bg-purple-500/10 text-purple-400" />
        <StatCard label="Visiteurs uniques" value={summary.unique_visitors ?? 0} sub={`${summary.new_visitors ?? 0} nouveaux, ${summary.returning_visitors ?? 0} récurrents`} icon={Users} color="bg-[#3b82f6]/10 text-[#3b82f6]" />
        <StatCard label="Taux conversion" value={`${summary.conversion_rate ?? 0}%`} sub={`${summary.total_requests ?? 0} demandes total`} icon={TrendingUp} color="bg-green-500/10 text-green-400" />
        <StatCard label="Clics CTA (30j)" value={summary.cta_clicks ?? 0} icon={MousePointerClick} color="bg-amber-500/10 text-amber-400" />
        <StatCard label="Bots bloqués" value={summary.bot_views ?? 0} sub="Exclus des statistiques" icon={Bot} color="bg-red-500/10 text-red-400" />
      </div>

      {/* Device breakdown */}
      {summary.devices && Object.keys(summary.devices).length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          {[
            { key: 'desktop', label: 'Desktop', icon: Monitor, color: 'bg-[#3b82f6]/10 text-[#3b82f6]' },
            { key: 'mobile', label: 'Mobile', icon: Smartphone, color: 'bg-purple-500/10 text-purple-400' },
            { key: 'tablet', label: 'Tablette', icon: Tablet, color: 'bg-amber-500/10 text-amber-400' },
          ].map(d => {
            const count = summary.devices[d.key] || 0;
            const total = Object.values(summary.devices).reduce((a, b) => a + b, 0);
            const pct = total > 0 ? Math.round(count / total * 100) : 0;
            return (
              <div key={d.key} className="p-4 rounded-xl border border-white/5 bg-[#0f0f17] flex items-center gap-4" data-testid={`device-${d.key}`}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${d.color}`}>
                  <d.icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[#f5f5f5] font-bold text-lg">{pct}%</p>
                  <p className="text-[#71717a] text-xs">{d.label} ({count})</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Trend Chart with filters */}
      <div className="rounded-xl border border-white/5 bg-[#0f0f17] p-5">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
          <SectionTitle>Tendance des visites</SectionTitle>
          <div className="flex flex-wrap items-center gap-2">
            {/* Page filter */}
            <div className="flex items-center gap-1.5">
              <Filter className="w-3.5 h-3.5 text-[#52525b]" />
              <select
                value={pageFilter}
                onChange={(e) => setPageFilter(e.target.value)}
                className="bg-[#1a1a2e] border border-white/10 text-[#a1a1aa] text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-[#3b82f6]"
                data-testid="page-filter"
              >
                <option value="">Toutes les pages</option>
                {pageOptions.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            {/* Period buttons */}
            {!customRange && (
              <div className="flex gap-1">
                {[7, 14, 30].map(d => (
                  <button
                    key={d}
                    onClick={() => { setPeriod(d); setCustomRange(false); }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      period === d && !customRange ? 'bg-[#3b82f6] text-white' : 'bg-[#1a1a2e] text-[#71717a] hover:text-[#a1a1aa]'
                    }`}
                    data-testid={`trend-period-${d}`}
                  >
                    {d}j
                  </button>
                ))}
              </div>
            )}

            {/* Custom date range */}
            <div className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-[#52525b]" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setCustomRange(true); }}
                className="bg-[#1a1a2e] border border-white/10 text-[#a1a1aa] text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-[#3b82f6]"
                data-testid="date-start"
              />
              <span className="text-[#52525b] text-xs">—</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setCustomRange(true); }}
                className="bg-[#1a1a2e] border border-white/10 text-[#a1a1aa] text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-[#3b82f6]"
                data-testid="date-end"
              />
              {customRange && (
                <button
                  onClick={() => { setCustomRange(false); setStartDate(''); setEndDate(''); }}
                  className="text-[#71717a] text-xs hover:text-red-400 transition-colors"
                >
                  Réinitialiser
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={trendFormatted}>
              <defs>
                <linearGradient id="gradViews" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradUnique" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a1a2e" />
              <XAxis dataKey="label" tick={{ fill: '#52525b', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#52525b', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="views" name="Vues" stroke="#3b82f6" fill="url(#gradViews)" strokeWidth={2} />
              <Area type="monotone" dataKey="unique" name="Uniques" stroke="#8b5cf6" fill="url(#gradUnique)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Funnel */}
        <div className="rounded-xl border border-white/5 bg-[#0f0f17] p-5">
          <SectionTitle>Funnel de conversion (30j)</SectionTitle>
          <div className="space-y-3 mt-4">
            {funnelData.map((step, i) => (
              <div key={step.name} data-testid={`funnel-step-${i}`}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-[#a1a1aa]">{step.name}</span>
                  <span className="text-[#f5f5f5] font-medium">{step.value} <span className="text-[#52525b]">({step.pct}%)</span></span>
                </div>
                <div className="w-full h-2 bg-[#1a1a2e] rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-700" style={{
                    width: `${Math.max(step.pct, 2)}%`,
                    background: i === 0 ? '#3b82f6' : i === 1 ? '#8b5cf6' : i === 2 ? '#f59e0b' : '#22c55e',
                  }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Pages */}
        <div className="rounded-xl border border-white/5 bg-[#0f0f17] p-5">
          <SectionTitle>Pages les plus vues (30j)</SectionTitle>
          <div className="space-y-2 max-h-64 overflow-y-auto mt-4">
            {summary.pages?.length > 0 ? summary.pages.map((p, i) => {
              const maxV = summary.pages[0]?.views || 1;
              return (
                <div key={i}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-[#a1a1aa] truncate max-w-[200px]">{p.page}</span>
                    <div className="flex gap-3 text-xs">
                      <span className="text-[#f5f5f5]">{p.views} <span className="text-[#52525b]">vues</span></span>
                      <span className="text-[#71717a]">{p.unique} <span className="text-[#52525b]">uniq.</span></span>
                      {p.avg_time > 0 && <span className="text-[#71717a]">{p.avg_time}s</span>}
                    </div>
                  </div>
                  <div className="w-full h-1 bg-[#1a1a2e] rounded-full overflow-hidden">
                    <div className="h-full bg-[#3b82f6] rounded-full" style={{ width: `${(p.views / maxV) * 100}%` }} />
                  </div>
                </div>
              );
            }) : <p className="text-[#52525b] text-sm">Aucune donnée.</p>}
          </div>
        </div>
      </div>

      {/* Heatmap */}
      <div className="rounded-xl border border-white/5 bg-[#0f0f17] p-5">
        <SectionTitle>Heures de pointe (30j)</SectionTitle>
        <p className="text-[#52525b] text-xs mb-4">Répartition des visites par jour et par heure</p>
        <Heatmap data={heatmap} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Geo */}
        <div className="rounded-xl border border-white/5 bg-[#0f0f17] p-5">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-4 h-4 text-[#3b82f6]" />
            <SectionTitle>Géolocalisation (30j)</SectionTitle>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[#71717a] text-xs uppercase tracking-wider mb-3">Pays</p>
              {geo?.countries?.length > 0 ? geo.countries.map((c, i) => (
                <div key={i} className="flex justify-between py-1.5 text-sm border-b border-white/5 last:border-0">
                  <span className="text-[#a1a1aa]">{c.name}</span>
                  <span className="text-[#f5f5f5] font-medium">{c.count}</span>
                </div>
              )) : <p className="text-[#52525b] text-xs">Aucune donnée.</p>}
            </div>
            <div>
              <p className="text-[#71717a] text-xs uppercase tracking-wider mb-3">Villes</p>
              {geo?.cities?.length > 0 ? geo.cities.map((c, i) => (
                <div key={i} className="flex justify-between py-1.5 text-sm border-b border-white/5 last:border-0">
                  <span className="text-[#a1a1aa]">{c.name}</span>
                  <span className="text-[#f5f5f5] font-medium">{c.count}</span>
                </div>
              )) : <p className="text-[#52525b] text-xs">Aucune donnée.</p>}
            </div>
          </div>
        </div>

        {/* Referrers */}
        <div className="rounded-xl border border-white/5 bg-[#0f0f17] p-5">
          <SectionTitle>Sources de trafic (30j)</SectionTitle>
          <div className="mt-4">
            {summary.referrers?.length > 0 ? (
              <div className="space-y-2">
                {summary.referrers.map((r, i) => {
                  let domain = r.source;
                  try { domain = new URL(r.source).hostname; } catch {}
                  return (
                    <div key={i} className="flex justify-between py-1.5 text-sm border-b border-white/5 last:border-0">
                      <span className="text-[#a1a1aa] truncate max-w-[250px]">{domain}</span>
                      <span className="text-[#f5f5f5] font-medium">{r.count}</span>
                    </div>
                  );
                })}
              </div>
            ) : <p className="text-[#52525b] text-sm">Aucun referrer détecté. Le trafic est principalement direct.</p>}
          </div>
        </div>
      </div>

      {/* Visitors */}
      <div className="rounded-xl border border-white/5 bg-[#0f0f17] p-5">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-4 h-4 text-[#3b82f6]" />
          <SectionTitle>Derniers visiteurs</SectionTitle>
        </div>
        {visitors.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="visitors-table">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left text-[#71717a] text-xs font-medium uppercase tracking-wider px-3 py-2">ID</th>
                  <th className="text-left text-[#71717a] text-xs font-medium uppercase tracking-wider px-3 py-2">Type</th>
                  <th className="text-left text-[#71717a] text-xs font-medium uppercase tracking-wider px-3 py-2">Device</th>
                  <th className="text-left text-[#71717a] text-xs font-medium uppercase tracking-wider px-3 py-2">Localisation</th>
                  <th className="text-left text-[#71717a] text-xs font-medium uppercase tracking-wider px-3 py-2">Pages</th>
                  <th className="text-left text-[#71717a] text-xs font-medium uppercase tracking-wider px-3 py-2">Source</th>
                  <th className="text-left text-[#71717a] text-xs font-medium uppercase tracking-wider px-3 py-2">Dernière visite</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {visitors.map((v) => (
                  <tr key={v.visitor_id} className="hover:bg-[#1a1a2e]/50 transition-colors" data-testid={`visitor-${v.visitor_id}`}>
                    <td className="px-3 py-2.5 font-mono text-[#f5f5f5]">{v.visitor_id}</td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                        v.is_new ? 'bg-green-500/10 text-green-400' : 'bg-[#3b82f6]/10 text-[#3b82f6]'
                      }`}>
                        {v.is_new ? 'Nouveau' : 'Récurrent'}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-[#71717a] text-xs capitalize">{v.device || 'desktop'}</td>
                    <td className="px-3 py-2.5 text-[#a1a1aa]">{v.city ? `${v.city}, ${v.country}` : v.country || '-'}</td>
                    <td className="px-3 py-2.5 text-[#f5f5f5]">{v.total_views}</td>
                    <td className="px-3 py-2.5 text-[#71717a] truncate max-w-[150px]">{v.referrer ? (() => { try { return new URL(v.referrer).hostname; } catch { return v.referrer; } })() : 'Direct'}</td>
                    <td className="px-3 py-2.5 text-[#71717a] text-xs">{new Date(v.last_seen).toLocaleString('fr-FR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-[#52525b] text-sm">Aucun visiteur enregistré.</p>}
      </div>
    </div>
  );
};

export default AdminAnalytics;
